import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { ReactNode, useState } from 'react'
import { ButtonProps } from 'rsuite'
import { Capacitor } from '@capacitor/core'
import { useAuth } from '../../../hooks/useAuth'
import { useHideTabBar } from '../../../hooks/useHideTabBar'
import { LogoutFilled, NotificationBell, Profile, SecondEmail } from '../../../utils/constants/icons'
import AccountSettingsPage from './AccountSettings'
import NotificationSettingsPage from './NotificationSettings'
import ProfilePage from './Profile'

type SettingsPage = 'account' | 'profile' | 'notifications'

interface DesktopNavButtonProps extends ButtonProps {
  active: boolean
  onClick: () => void
  icon: ReactNode
  text: string
}

const DesktopNavButton: React.FC<DesktopNavButtonProps> = ({ active, onClick, icon, text }) => {
  return (
    <Button
      bg={active ? 'white' : 'neutral.10'}
      borderColor="transparent"
      borderWidth="1px"
      _hover={
        active
          ? { bg: 'white', border: '1px solid white' }
          : { bg: 'neutral.50', borderWidth: '1px', borderColor: 'neutral.50' }
      }
      _focus={{
        borderWidth: '1px',
        borderColor: 'primary.500',
        boxShadow: '0 0 0 2px #CFCDEB',
      }}
      mb="4px"
      padding="12px"
      height="45px"
      width="208px"
      onClick={onClick}
    >
      <Text color={active ? 'neutral.900' : 'neutral.500'} fontWeight="400" textStyle="b-sm" mr="auto">
        {text}
      </Text>
      {icon}
    </Button>
  )
}

const MobileSegmentedNav: React.FC<{
  activePage: SettingsPage
  onChange: (page: SettingsPage) => void
}> = ({ activePage, onChange }) => {
  const items: { key: SettingsPage; label: string; icon: ReactNode }[] = [
    {
      key: 'account',
      label: 'Account',
      icon: (
        <Icon w="16px" h="16px" viewBox="0 0 16 16">
          <SecondEmail />
        </Icon>
      ),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: (
        <Icon w="16px" h="16px" viewBox="0 0 16 16">
          <Profile />
        </Icon>
      ),
    },
    {
      key: 'notifications',
      label: 'Alerts',
      icon: (
        <Icon w="16px" h="16px" viewBox="0 0 24 24">
          <NotificationBell />
        </Icon>
      ),
    },
  ]

  return (
    <Flex
      bg="neutral.10"
      borderRadius="12px"
      p="4px"
      gap="4px"
      mb="20px"
      role="tablist"
      aria-label="Settings sections"
    >
      {items.map((item) => {
        const isActive = activePage === item.key
        return (
          <Button
            key={item.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(item.key)}
            flex="1"
            h="44px"
            borderRadius="8px"
            bg={isActive ? 'white' : 'transparent'}
            color={isActive ? 'primary.500' : 'neutral.500'}
            fontWeight={isActive ? '510' : '400'}
            fontSize="14px"
            boxShadow={isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
            transition="all 0.15s ease-out"
            _hover={{ bg: isActive ? 'white' : 'neutral.50' }}
            _active={{ transform: 'scale(0.98)' }}
            leftIcon={
              <Box color={isActive ? 'primary.500' : 'neutral.400'}>
                {item.icon}
              </Box>
            }
            px="8px"
          >
            {item.label}
          </Button>
        )
      })}
    </Flex>
  )
}

const LogoutButton = ({ variant = 'desktop' }: { variant?: 'desktop' | 'mobile' }) => {
  const { logout, logoutNative } = useAuth()
  const isNative = Capacitor.isNativePlatform()
  const handleLogout = () => (isNative ? logoutNative() : logout())

  if (variant === 'mobile') {
    return (
      <Button
        w="100%"
        h="48px"
        bg="white"
        borderWidth="1px"
        borderColor="#FBE5DD"
        borderRadius="10px"
        color="#E4502F"
        fontWeight="510"
        fontSize="14px"
        _hover={{ bg: '#FFF5F1' }}
        _active={{ transform: 'scale(0.99)' }}
        onClick={handleLogout}
        leftIcon={
          <Icon width="16px" height="16px" viewBox="0 0 16 16" color="#E4502F">
            <LogoutFilled />
          </Icon>
        }
      >
        Logout
      </Button>
    )
  }

  return (
    <Button
      bgColor="neutral.10"
      padding="12px"
      borderWidth="0"
      height="45px"
      pos="absolute"
      bottom="20px"
      left="20px"
      width="208px"
      onClick={handleLogout}
      _focus={{
        borderWidth: '1px',
        borderColor: 'secondary.500',
        boxShadow: '0 0 0 2px #CFCDEB',
      }}
      leftIcon={
        <Icon width="16px" height="16px" viewBox="0 0 16 16" color="#E4502F">
          <LogoutFilled />
        </Icon>
      }
    >
      <Text color="#E4502F" fontWeight="400" textStyle="b-sm" mr="auto">
        Logout
      </Text>
    </Button>
  )
}

const ProfileModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [activePage, setActivePage] = useState<SettingsPage>('account')
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  useHideTabBar(isOpen, 'settings-modal')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={!isMobile}
      scrollBehavior="inside"
      size={{ base: 'full', md: 'md' }}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent
        height={{ base: '100dvh', md: '795px' }}
        maxH={{ base: '100dvh', md: '90vh' }}
        maxW={{ base: '100%', md: '1104px' }}
        margin={{ base: 0, md: '32px' }}
        borderRadius={{ base: 0, md: '24px' }}
        display="flex"
        flexDir={{ base: 'column', md: 'row' }}
        padding="0px"
        overflow="hidden"
      >
        {isMobile ? (
          <Flex
            direction="column"
            flex="1"
            minH="0"
            pt="calc(env(safe-area-inset-top) + 16px)"
            pb="calc(env(safe-area-inset-bottom) + 16px)"
            px="16px"
            bg="white"
          >
            <Flex align="center" justify="space-between" mb="16px" pr="44px">
              <Text fontSize="13px" fontWeight="510" color="neutral.300" letterSpacing="0.06em">
                SETTINGS
              </Text>
            </Flex>

            <ModalCloseButton
              top="calc(env(safe-area-inset-top) + 12px)"
              right="12px"
              w="40px"
              h="40px"
              borderRadius="10px"
              color="neutral.500"
              fontSize="16px"
              zIndex="2"
              _hover={{ bg: 'neutral.100', color: 'neutral.700' }}
            />

            <MobileSegmentedNav activePage={activePage} onChange={setActivePage} />

            <Box flex="1" minH="0" overflowY="auto" mx="-16px" px="16px">
              {activePage === 'account' && <AccountSettingsPage />}
              {activePage === 'profile' && <ProfilePage />}
              {activePage === 'notifications' && <NotificationSettingsPage />}
            </Box>

            <Box pt="12px" mt="auto">
              <LogoutButton variant="mobile" />
            </Box>
          </Flex>
        ) : (
          <>
            <Box
              bgColor="neutral.10"
              maxW="240px"
              minW="240px"
              borderLeftRadius="24px"
              padding="24px 16px"
              overflowY="auto"
            >
              <Text color="neutral.300" textStyle="b-md" mb="16px">
                SETTINGS
              </Text>
              <DesktopNavButton
                active={activePage === 'account'}
                onClick={() => setActivePage('account')}
                icon={
                  <Icon
                    width="16px"
                    height="16px"
                    viewBox="0 0 16 16"
                    color={activePage === 'account' ? 'primary.500' : 'neutral.200'}
                  >
                    <SecondEmail />
                  </Icon>
                }
                text="Account Settings"
              />
              <DesktopNavButton
                active={activePage === 'profile'}
                onClick={() => setActivePage('profile')}
                icon={
                  <Icon
                    width="16px"
                    height="16px"
                    viewBox="0 0 16 16"
                    color={activePage === 'profile' ? 'primary.500' : 'neutral.200'}
                  >
                    <Profile />
                  </Icon>
                }
                text="Profile"
              />
              <DesktopNavButton
                active={activePage === 'notifications'}
                onClick={() => setActivePage('notifications')}
                icon={
                  <Icon
                    width="16px"
                    height="16px"
                    viewBox="0 0 24 24"
                    color={activePage === 'notifications' ? 'primary.500' : 'neutral.200'}
                  >
                    <NotificationBell />
                  </Icon>
                }
                text="Notification Settings"
              />
              <LogoutButton />
            </Box>
            {activePage === 'account' && <AccountSettingsPage />}
            {activePage === 'profile' && <ProfilePage />}
            {activePage === 'notifications' && <NotificationSettingsPage />}
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default ProfileModal
